import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import GoogleSignInButton from './GoogleSignInButton';
import { consumeSessionSupersededFlash } from '../services/api';
import './LoginModal.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const LoginModal = ({ isOpen, onClose, onLoginSuccess }) => {
  const navigate = useNavigate();
  const [isSignup, setIsSignup] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    role: 'student'
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    const flash = consumeSessionSupersededFlash();
    if (flash) {
      setError(flash);
    }
  }, [isOpen]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: (formData.email || '').trim().toLowerCase(),
          password: formData.password
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        if (data.token) {
          localStorage.setItem('token', data.token);
        }
        if (data.refreshToken) {
          localStorage.setItem('refreshToken', data.refreshToken);
        }
        if (data.user) {
          localStorage.setItem('user', JSON.stringify(data.user));
        }
        
        if (onLoginSuccess) {
          onLoginSuccess(data.user);
        }
        onClose();
      } else {
        setError(data.message || 'Invalid email or password');
      }
    } catch (err) {
      console.error('Login error:', err);
      setError('Failed to connect to server. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSuccess = useCallback(async (idToken) => {
    setError('');
    setGoogleLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/google`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id_token: idToken })
      });
      const data = await response.json();
      if (response.ok && data.success) {
        if (data.token) localStorage.setItem('token', data.token);
        if (data.refreshToken) localStorage.setItem('refreshToken', data.refreshToken);
        if (data.user) localStorage.setItem('user', JSON.stringify(data.user));
        if (onLoginSuccess) onLoginSuccess(data.user);
        onClose();
      } else {
        setError(data.message || 'Google sign-in failed. Please try again.');
      }
    } catch (err) {
      console.error('Google sign-in error:', err);
      setError('Could not connect. Please try again.');
    } finally {
      setGoogleLoading(false);
    }
  }, [onLoginSuccess, onClose]);

  const handleSignup = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          password: formData.password,
          role: formData.role
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Auto-login after signup
        await handleLogin(e);
      } else {
        setError(data.message || 'Signup failed. Please try again.');
      }
    } catch (err) {
      console.error('Signup error:', err);
      setError('Failed to connect to server. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="login-modal-overlay" onClick={onClose}>
      <div className="login-modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="login-modal-close" onClick={onClose}>
          <span className="material-icons">close</span>
        </button>
        
        <div className="login-modal-header">
          <h2>{isSignup ? 'Create Account' : 'Login Required'}</h2>
          <p>{isSignup ? 'Sign up to download Word documents' : 'Please login to download Word documents'}</p>
        </div>

        <div className="login-modal-tabs">
          <button 
            className={!isSignup ? 'active' : ''} 
            onClick={() => setIsSignup(false)}
          >
            Login
          </button>
          <button 
            className={isSignup ? 'active' : ''} 
            onClick={() => setIsSignup(true)}
          >
            Sign Up
          </button>
        </div>

        <form onSubmit={isSignup ? handleSignup : handleLogin} className="login-modal-form">
          {error && (
            <div className="error-message">
              <span className="material-icons">error</span>
              {error}
            </div>
          )}

          {isSignup && (
            <div className="input-group">
              <span className="material-icons">person</span>
              <input
                type="text"
                name="name"
                placeholder="Full Name"
                value={formData.name}
                onChange={handleInputChange}
                required
              />
            </div>
          )}

          <div className="input-group">
            <span className="material-icons">email</span>
            <input
              type="email"
              name="email"
              placeholder="Email"
              value={formData.email}
              onChange={handleInputChange}
              required
            />
          </div>

          <div className="input-group">
            <span className="material-icons">lock</span>
            <input
              type="password"
              name="password"
              placeholder="Password"
              value={formData.password}
              onChange={handleInputChange}
              required
            />
          </div>

          {isSignup && (
            <div className="input-group">
              <span className="material-icons">school</span>
              <select
                name="role"
                value={formData.role}
                onChange={handleInputChange}
                required
              >
                <option value="student">Student</option>
                <option value="teacher">Teacher</option>
                <option value="parent">Parent</option>
              </select>
            </div>
          )}

          <button type="submit" className="btn-primary full-width" disabled={loading}>
            {loading ? 'Please wait...' : (isSignup ? 'Sign Up' : 'Login')}
          </button>

          {process.env.REACT_APP_GOOGLE_CLIENT_ID && (
            <>
              <div className="login-modal-divider">
                <span>or</span>
              </div>
              <div className="login-modal-google">
                <GoogleSignInButton
                  onSuccess={handleGoogleSuccess}
                  onError={() => setError('Google sign-in could not start. Check your connection.')}
                  disabled={loading || googleLoading}
                  className="login-google-btn-wrap"
                  text={isSignup ? 'signup_with' : 'signin_with'}
                />
                {googleLoading && <p className="login-google-loading">Signing in…</p>}
              </div>
            </>
          )}
        </form>

        <div className="login-modal-footer">
          {!isSignup && (
            <p>
              Don't have an account?{' '}
              <a href="#" onClick={(e) => { e.preventDefault(); setIsSignup(true); }}>
                Sign up
              </a>
            </p>
          )}
          {isSignup && (
            <p>
              Already have an account?{' '}
              <a href="#" onClick={(e) => { e.preventDefault(); setIsSignup(false); }}>
                Login
              </a>
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginModal;
